import React, { useState, useEffect } from 'react';
import { Button, Form, Upload, Modal, App, Input } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import styles from './ProductDetailTemplate2.module.scss';
import type { UploadFile, UploadProps } from 'antd';
import { useAppSelector } from '@/store/hooks';
import TextArea from 'antd/es/input/TextArea';
import icon1 from '@/assets/images/templete2.jpg';
import { useTranslation } from 'react-i18next';

interface ImageUrl {
  url: string;
  name: string;
}

export interface ProductDetailTemplate2Props {
  data: {
    name: string;
    title: string;
    content: string;
    imgUrl: ImageUrl[];
  };
  onChange?: (data: any,selfIndex:number) => void;
  editable?: boolean;
  selfIndex?: number;
  thumbnailMode?: boolean;//是否是缩略图模式
  onDelete?: (selfIndex: number) => void;
}

const ProductDetailTemplate2: React.FC<ProductDetailTemplate2Props> = ({  
  data,
  onChange,
  editable = true,
  selfIndex = 0,
  thumbnailMode = false,
  onDelete
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation();
  // 预览状态
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  // 编辑表单数据
  const [title, setTitle] = useState(data?.title || '');
  const [content, setContent] = useState(data?.content || '');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 获取token
  const token = useAppSelector((state) => state.user.token);

  // 初始化数据
  useEffect(() => {
    if (data) {
      setTitle(data.title || '');
      setContent(data.content || '');

      // 设置图片列表
      if (data.imgUrl && data.imgUrl.length > 0) {
        const images = data.imgUrl.map((item, index) => ({
          uid: `-${index}`,
          name: item.name || `image-${index}`,
          status: 'done' as const,
          url: item.url
        }));
        setFileList(images);
      }
    }
  }, [data]);

  // 图片上传配置
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: `${token}`
    },
    listType: 'picture-card',
    fileList: fileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('showCase.Image10MB'));
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      // 复制一份文件列表
      const newFileList = [...info.fileList];

      // 对文件状态进行处理
      newFileList.forEach(file => {
        if (file.response) {
          file.url = file?.response?.data?.url;
        }
      });

      // 更新状态
      setFileList(newFileList);

      if (info.file.status === 'done') {
        message.success(`${info.file.name} ${t('showCase.uploadedsuccessfully')}`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} ${t('showCase.uploadfailed')}`);
      }
    },
    onPreview: async (file) => {
      let src = file.url as string;
      if (!src) {
        src = await new Promise(resolve => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as any);
          reader.onload = () => resolve(reader.result as string);
        });
      }
      // 使用 Modal 预览而不是 window.open 来避免跨域问题
      setPreviewImage(src);
      setPreviewVisible(true);
    }
  };

  // 打开编辑模式
  const handleEdit = () => {
    setIsEditing(true);
    form.setFieldsValue({
      title,
      content
    });
  };

  // 保存编辑
  const handleSave = () => {
    form.validateFields().then(values => {
      const updatedData = {
        ...data,
        title: values.title,
        content: values.content,
        imgUrl: fileList.map(file => ({
          url: file.url || '',
          name: file.name || ''
        }))
      };

      onChange && onChange(updatedData,selfIndex);
      setTitle(values.title);
      setContent(values.content);
      setIsEditing(false);
      message.success(t('showCase.Templateupdatedsuccessfully'));
    });
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    // 恢复原始数据
    if (data) {
      setFileList(
        data.imgUrl.map((item, index) => ({
          uid: `-${index}`,
          name: item.name || `image-${index}`,
          status: 'done' as const,
          url: item.url
        }))
      );
    }
  };

  // 上传按钮
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t('showCase.Upload')}</div>
    </div>
  );

  return (
    <div className={styles['template-container']}>
      {/* 展示模式 */}


      {thumbnailMode ? (
        <div className={styles['template-thumbnail']} onClick={handleEdit}>
          <div className={styles['thumbnail-number']}>
            {selfIndex + 1}
          </div>
          <img className={styles['thumbnail-image']} src={icon1 || ''} alt="" />
          <div className={styles['thumbnail-actions']}>
            <Button 
              size="small"
              danger
              className={styles['delete-btn']}
              onClick={(e) => {
                e.stopPropagation();
                // 这里需要调用删除函数，但需要从父组件传入
                onDelete && onDelete(selfIndex);
              }}
            >
              {t('showCase.Delete')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles['template-content']}>
          <img className={styles['template-image']} src={fileList[0]?.url || ''} alt="" />
          <div className={styles['template-container-box']}>
            <div className={styles['text-container']}>
              <div className={styles['template-title']}>{title}</div>
              <div className={styles['template-description']}>{content}</div>
            </div>
          </div>
            
        </div>
      )}

      

      {/* 编辑模式 */}
      {isEditing && (
        <Modal
          title={t('showCase.EditTemplate')}
          open={isEditing}
          onOk={handleSave}
          onCancel={handleCancel}
          okText={t('showCase.Save')}
          cancelText={t('membership.Cancel')}
          className={styles['edit-modal']}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              title,
              content
            }}
          >
            <Form.Item
              label={t('showCase.Title')}
              name="title"
              rules={[{ required: true, message: t('showCase.Pleaseentertitle') }]}
            >
              <Input placeholder={t('showCase.Pleaseentertitle')} maxLength={50} />
            </Form.Item>

            <Form.Item
              label={t('showCase.Content')}
              name="content"
              rules={[{ required: true, message: t('showCase.Pleaseentercontent') }]}
            >
              <TextArea
                placeholder={t('showCase.Pleaseentercontent')}
                rows={4}
                maxLength={200}
              />
            </Form.Item>

            <Form.Item
              label={t('showCase.Image')}
            >
              <Upload {...uploadProps}>
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>
              <div className={styles['upload-hint']}>
                {t('showCase.PleaseRecommended')}
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* 图片预览 Modal */}
      <Modal
        open={previewVisible}
        title={t('showCase.ImagePreview')}
        footer={null}
        width={'80%'}
        onCancel={() => setPreviewVisible(false)}
        zIndex={2000} 
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default ProductDetailTemplate2; 